import { useEffect, useState, useCallback } from "react";
import { Play, Pause, SkipForward, Volume2, VolumeX, ListMusic, X } from "lucide-react";
import { Faixa } from "@/types/playlist";
import { listarPlaylist, extrairVideoId } from "@/lib/playlist";
import { useYouTubePlayer } from "@/hooks/useYouTubePlayer";

interface Props {
  containerId: string;
  onVideoIdChange?: (videoId: string) => void;
  hideControls?: boolean;
}

export default function PlayerBar({ containerId, onVideoIdChange, hideControls = false }: Props) {
  const [faixas, setFaixas] = useState<Faixa[]>([]);
  const [indice, setIndice] = useState(0);
  const [playlistAberta, setPlaylistAberta] = useState(false);
  const [carregado, setCarregado] = useState(false);

  useEffect(() => {
    listarPlaylist().then((lista) => { setFaixas(lista); setCarregado(true); });
  }, []);

  const faixaAtual = faixas[indice] ?? null;
  const videoId = faixaAtual ? extrairVideoId(faixaAtual.linkYoutube) : "";

  useEffect(() => {
    if (videoId) onVideoIdChange?.(videoId);
  }, [videoId, onVideoIdChange]);

  const pularFaixa = useCallback(() => {
    setIndice((i) => (i + 1) % faixas.length);
    setPlaylistAberta(false);
  }, [faixas.length]);

  const irPara = useCallback((i: number) => {
    setIndice(i);
    setPlaylistAberta(false);
  }, []);

  const { state, muted, currentTime, duration, togglePlay, seek, toggleMute } =
    useYouTubePlayer({ containerId, videoId, startMuted: true, onEnded: pularFaixa });

  const isPlaying = state === "playing" || state === "buffering";
  const progresso = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!carregado || faixas.length === 0) return null;

  if (hideControls) return null;

  return (
    <>
      {/* Barra do player */}
      <div 
        className="noise fixed bottom-0 left-0 right-0 z-50 border-t" 
        style={{ 
          backgroundColor: "#DA422F", 
          borderColor: "#F8EBC133",
          position: "fixed"
        }}
      >

        {/* Barra de progresso no topo — clicável */}
        <div
          className="w-full h-1 cursor-pointer group"
          style={{ backgroundColor: "#F8EBC133" }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            seek(((e.clientX - rect.left) / rect.width) * duration);
          }}
        >
          <div
            className="h-full group-hover:opacity-100 transition-all duration-200"
            style={{ width: `${progresso}%`, backgroundColor: "#F8EBC1", opacity: 0.7 }}
          />
        </div>

        {/* Conteúdo dentro de container centralizado */}
        <div className="max-w-6xl mx-auto px-8 h-20 flex items-center gap-6">

          {/* Label */}
          <span className="hidden lg:block text-[10px] font-bold tracking-[0.25em] uppercase shrink-0 whitespace-nowrap" style={{ color: "#F8EBC166" }}>
            Playlist Oficial
          </span>

          {/* Controles */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={togglePlay}
              className="w-11 h-11 rounded-full flex items-center justify-center hover:opacity-90 transition shrink-0"
              style={{ backgroundColor: "#F8EBC1" }}
              aria-label={isPlaying ? "Pausar" : "Reproduzir"}
            >
              {isPlaying
                ? <Pause className="w-4 h-4 fill-[#DA422F]" style={{ color: "#DA422F" }} />
                : <Play  className="w-4 h-4 fill-[#DA422F] ml-0.5" style={{ color: "#DA422F" }} />}
            </button>

            <button
              onClick={pularFaixa}
              className="w-9 h-9 flex items-center justify-center hover:opacity-70 transition"
              style={{ color: "#F8EBC1" }}
              aria-label="Próxima faixa"
            >
              <SkipForward className="w-5 h-5" style={{ fill: "#F8EBC1" }} />
            </button>
          </div>

          {/* Nome + artista */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold uppercase tracking-widest truncate leading-tight" style={{ color: "#F8EBC1" }}>
              {faixaAtual?.musica}
              <span className="font-normal" style={{ color: "#F8EBC166" }}> — {faixaAtual?.artista}</span>
            </p>
          </div>

          {/* Playlist */}
          <button
            onClick={() => setPlaylistAberta((v) => !v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold tracking-widest uppercase transition shrink-0`}
            style={playlistAberta 
              ? { backgroundColor: "#F8EBC1", color: "#DA422F" }
              : { color: "#F8EBC166", borderWidth: "1px", borderColor: "#F8EBC133" }
            }
          >
            <ListMusic className="w-4 h-4" />
            <span className="hidden sm:inline">Lista</span>
          </button>

          {/* Mudo / Som */}
          <button
            onClick={toggleMute}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold tracking-widest uppercase transition shrink-0`}
            style={!muted
              ? { backgroundColor: "#F8EBC1", color: "#DA422F" }
              : { color: "#F8EBC166", borderWidth: "1px", borderColor: "#F8EBC133" }
            }
            aria-label={muted ? "Ativar som" : "Desativar som"}
          >
            {muted
              ? <><VolumeX className="w-4 h-4" /><span className="hidden sm:inline">Som</span></>
              : <><Volume2 className="w-4 h-4" /><span className="hidden sm:inline">Som</span></>}
          </button>
        </div>
      </div>

      {/* Drawer de playlist */}
      {playlistAberta && (
        <div 
          className="noise fixed bottom-20 right-0 w-full sm:w-80 border border-b-0 sm:rounded-tl-2xl overflow-hidden shadow-2xl" 
          style={{ 
            backgroundColor: "#DA422F", 
            borderColor: "#F8EBC133",
            zIndex: 100,
            position: "fixed"
          }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#F8EBC133" }}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: "#F8EBC166" }}>
              Playlist Oficial
            </p>
            <button
              onClick={() => setPlaylistAberta(false)}
              className="w-7 h-7 flex items-center justify-center rounded-lg transition"
              style={{ color: "#F8EBC166" }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#F8EBC11A"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {faixas.map((f, i) => (
              <li key={f.id}>
                <button
                  onClick={() => irPara(i)}
                  className="w-full flex items-center gap-3 px-5 py-3 text-left transition"
                  style={i === indice ? { backgroundColor: "#F8EBC11A" } : {}}
                  onMouseEnter={(e) => { if (i !== indice) e.currentTarget.style.backgroundColor = "#F8EBC10D"; }}
                  onMouseLeave={(e) => { if (i !== indice) e.currentTarget.style.backgroundColor = "transparent"; }}
                >
                  <img
                    src={`https://img.youtube.com/vi/${extrairVideoId(f.linkYoutube)}/default.jpg`}
                    alt=""
                    className="w-10 h-7 object-cover rounded shrink-0"
                    style={{ backgroundColor: "#F8EBC11A" }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-wide truncate" style={{ color: i === indice ? "#F8EBC1" : "#F8EBC199" }}>
                      {f.musica}
                    </p>
                    <p className="text-[11px] uppercase tracking-wider truncate" style={{ color: "#F8EBC166" }}>{f.artista}</p>
                  </div>
                  {i === indice && isPlaying && (
                    <span className="flex gap-0.5 items-end shrink-0">
                      {[1, 2, 3].map((b) => (
                        <span key={b} className="w-0.5 rounded-full animate-bounce"
                          style={{ height: `${6 + b * 3}px`, animationDelay: `${b * 0.15}s`, backgroundColor: "#F8EBC1" }} />
                      ))}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
